import { apiRequest } from './client';
import type { LotRepository } from '../../application/ports/LotRepository';
import type { CropPreset, Lot, LotInput, LotShare } from '../../domain/Lot';
import type { PendingQrInvite } from '../../domain/DeviceShare';

// La zona horaria del navegador define dónde corta cada día de grados día.
const tz = () => encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

export class HttpLotRepository implements LotRepository {
  presets() {
    return apiRequest<{ presets: CropPreset[] }>('/lots/presets');
  }

  list() {
    return apiRequest<{ lots: Lot[] }>(`/lots?tz=${tz()}`);
  }

  get(id: string) {
    return apiRequest<{ lot: Lot }>(`/lots/${id}?tz=${tz()}`);
  }

  create(input: LotInput) {
    return apiRequest<{ lot: Lot }>(`/lots?tz=${tz()}`, { method: 'POST', body: JSON.stringify(input) });
  }

  update(id: string, input: Partial<LotInput>) {
    return apiRequest<{ lot: Lot }>(`/lots/${id}?tz=${tz()}`, { method: 'PATCH', body: JSON.stringify(input) });
  }

  observeStage(id: string, stageIndex: number) {
    return apiRequest<{ lot: Lot }>(`/lots/${id}/observed-stage?tz=${tz()}`, { method: 'POST', body: JSON.stringify({ stageIndex }) });
  }

  delete(id: string) {
    return apiRequest<void>(`/lots/${id}`, { method: 'DELETE' });
  }

  listShares(id: string) {
    return apiRequest<{ shares: LotShare[]; pendingQrInvite: Omit<PendingQrInvite, 'id'> | null }>(`/lots/${id}/shares`);
  }

  createQrInvite(id: string, level: 'view' | 'control') {
    return apiRequest<{ token: string; level: 'view' | 'control'; expiresAt: string }>(`/lots/${id}/shares/qr`, {
      method: 'POST',
      body: JSON.stringify({ level }),
    });
  }

  share(id: string, email: string, level: 'view' | 'control') {
    return apiRequest<{ share: LotShare }>(`/lots/${id}/shares`, { method: 'POST', body: JSON.stringify({ email, level }) });
  }

  revokeShare(id: string, shareId: string) {
    return apiRequest<void>(`/lots/${id}/shares/${shareId}`, { method: 'DELETE' });
  }
}
